import axios from 'axios'
import Global from '@/global'
import { CACHE_KEY } from '@/utils/constant'
import { clone } from 'ramda'
import Vue from 'vue'
import { getUserByUid } from '@/api/user'

/**
 * @param page 页数
 * @param limit 每页的数量
 * @param userBasicList 用户基础信息列表
 * @param hash
 * @param isOwnCompany 区分是本企业/协作企业
 * @returns {Promise<unknown>}
 */
function queryUserBasicList (page, limit, userBasicList, hash, isOwnCompany) {
  return new Promise((resolve, reject) => {
    const userBasicListHash = `${CACHE_KEY.hash}_${isOwnCompany ? 'own' : 'ext'}`
    const userBasicListData = `${CACHE_KEY.user}_${isOwnCompany ? 'own' : 'ext'}`
    const fn = () => {
      if (!Vue.ss.get(userBasicListHash)) {
        const requestBaseUrl = `/hiapigw/app/coc/userBasicList?page=${page++}&limit=${limit}`
        const requestUrl = isOwnCompany ? requestBaseUrl : `${requestBaseUrl}&coc=true`
        return axios.get(requestUrl).then(res => {
          const { code, data: { hash: nHash, list, total } } = res.data
          if (code === 0) {
            if (hash && hash !== nHash) {
              hash = ''
              userBasicList = []
              page = 1
              fn()
            } else {
              hash = nHash
              userBasicList = userBasicList.concat(list)
              if ((page - 1) * limit < total) {
                fn()
              } else {
                Vue.ss.set(userBasicListHash, hash)
                Vue.ss.set(userBasicListData, userBasicList)
                resolve(userBasicList)
              }
            }
          }
        })
      } else {
        const requestBaseUrl = `/hiapigw/app/coc/userBasicList?page=${page++}&limit=${limit}&hash=${Vue.ss.get(userBasicListHash)}`
        const requestUrl = isOwnCompany ? requestBaseUrl : `${requestBaseUrl}&coc=true`
        return axios.get(requestUrl).then(res => {
          const { code, data: { hash: nHash, list } } = res.data
          if (code === 0 && list === null && nHash === Vue.ss.get(userBasicListHash)) {
            userBasicList = Vue.ss.get(userBasicListData)
            resolve(userBasicList)
          } else {
            Vue.ss.remove(userBasicListHash)
            Vue.ss.remove(userBasicListData)
            hash = ''
            userBasicList = []
            page = 1
            fn()
          }
        })
      }
    }
    fn()
  })
}

const setOnlinerAttrs = (target, origin) => {
  // eslint-disable-next-line no-prototype-builtins
  if (!origin.hasOwnProperty('deviceType')) return target
  return Object.assign(target, { roomId: origin.roomId, deviceType: origin.deviceType })
}
/**
 * @param originTree 树形结构数据
 * @param userBasicList 用户基础列表
 * @param level 当前的层次
 * @returns {{parentId}|*} 添加了用户的树形结构
 */
async function handleTree (originTree, userBasicList, level) {
  if (!originTree.parentId) { // 公司，加粗
    originTree.class = ['corp-node']
  } else {
    originTree.class = [`org-node-${level}`]
  }
  originTree.slots = { icon: originTree.parentId ? 'org' : 'corp' }
  if (Array.isArray(originTree.uids) && originTree.uids.length > 0) {
    const mapData = []
    for (let i = 0; i < originTree.uids.length; i++) {
      const el = originTree.uids[i]
      const found = userBasicList.find(ele => ele.uid === el)
      if (found) {
        mapData.push(setOnlinerAttrs({
          _index: i,
          name: found.name,
          orgId: `uid|${found.uid}`,
          orgList: [],
          avatar: found.avatar || null,
          scopedSlots: { icon: 'avatar', title: 'customTitle' },
          class: [`leaf-node-${level + 1}`]
        }, found))
      } else {
        let userList = (Vue.ss.get(`${CACHE_KEY.user}_own`) || [])
        let userInfo = userList.find(e => e.uid === el)
        if (userInfo) {
          mapData.push(setOnlinerAttrs({
            _index: i,
            name: userInfo.name,
            orgId: `uid|${userInfo.uid}`,
            orgList: [],
            avatar: userInfo.avatar || null,
            scopedSlots: { icon: 'avatar', title: 'customTitle' },
            class: [`leaf-node-${level + 1}`]
          }, userInfo))
        } else {
          const data = await getUserByUid(el)
          if (data) {
            userInfo = data.userInfo
            const obj = setOnlinerAttrs({
              _index: i,
              avatar: userInfo.avatar,
              corpId: userInfo.corpId,
              corpName: userInfo.corpName,
              email: userInfo.email,
              mobile: userInfo.mobile,
              name: userInfo.name,
              nameSpell: userInfo.nameSpell,
              uid: userInfo.userId
            }, userInfo)
            mapData.push(obj)
            userList = [...userList, { ...obj }]
            Vue.ss.set(`${CACHE_KEY.user}_own`, userList)
          }
        }
      }
    }
    originTree.orgList.push(...mapData)
    for (let i = 0; i <= originTree.orgList.length - 1 - mapData.length; i++) {
      await handleTree(originTree.orgList[i], userBasicList, level + 1)
    }
  } else {
    for (let i = 0; i <= originTree.orgList.length - 1; i++) {
      await handleTree(originTree.orgList[i], userBasicList, level + 1)
    }
  }
  return originTree
}

// 调整在线离线顺序
// eslint-disable-next-line no-prototype-builtins
const isOrg = row => row.hasOwnProperty('uids')
const getListIndex = list => {
  const length = list.filter(item => isOrg(item)).length
  return length || 0
}
const getUserIdFromOrgId = orgId => {
  const userIds = String(orgId).split('|')
  return userIds[userIds.length - 1]
}
const findIndexOfOrg = (list, row) => {
  const orgList = list.filter(item => !item.scopedSlots)
  const allList = list.filter(item => item.scopedSlots)
  if (allList.length === 0) return orgList.length

  const online = allList.filter(item => item.deviceType)
  const offline = allList.filter(item => !item.deviceType)
  if (row.deviceType) {
    if (online.length === 0) {
      return orgList.length
    } else if (online.length === 1) {
      if (row._index > online[0]._index) {
        return orgList.length + 1
      } else {
        return orgList.length
      }
    } else {
      for (let i = 0; i < online.length; i++) {
        if (row._index < online[i]._index) {
          return orgList.length + i
        }
      }
      return orgList.length + online.length
    }
  } else {
    if (offline.length === 0) {
      return orgList.length + online.length
    } else if (offline.length === 1) {
      if (row._index > offline[0]._index) {
        return orgList.length + online.length + 1
      } else {
        return orgList.length
      }
    } else {
      for (let i = 0; i < offline.length; i++) {
        if (row._index < offline[i]._index) {
          return orgList.length + online.length + i
        }
      }
      return orgList.length + online.length + offline.length
    }
  }
}

// 更新在线离线人员的位置
const setTreeDataOfOnlineAndOffline = (tree = [], userList = []) => {
  return tree.map(treeItem => {
    if (!isOrg(treeItem)) return treeItem

    let i = getListIndex(treeItem.orgList)
    const cache = new Set()

    while (i < treeItem.orgList.length) {
      const item = treeItem.orgList[i]
      if (cache.has(item.orgId)) {
        i++
        continue
      }
      const isOnOffUser = userList.find(onOffUser => String(onOffUser.userid) === String(getUserIdFromOrgId(item.orgId)))
      if (!isOnOffUser) {
        i++
        continue
      }
      const currentUser = treeItem.orgList.splice(i, 1)[0]
      cache.add(currentUser.orgId)
      if (isOnOffUser.status) {
        currentUser.deviceType = isOnOffUser.devicetype
        currentUser.roomId = isOnOffUser.roomid
        treeItem.orgList.splice(findIndexOfOrg(treeItem.orgList, currentUser), 0, currentUser)
      } else {
        delete currentUser.deviceType
        delete currentUser.roomId
        treeItem.orgList.splice(findIndexOfOrg(treeItem.orgList, currentUser), 0, currentUser)
      }
      i = getListIndex(treeItem.orgList)
    }

    return Object.assign(treeItem, { orgList: setTreeDataOfOnlineAndOffline(treeItem.orgList, userList) })
  })
}

const initialState = () => ({
  searchVal: '',
  searchCropVal: '',
  hasLoadTree: false,
  detailUserInfo: null,
  treeData: [],
  cropList: [],
  corpMapperTree: [],
  onlineLoading: false,
  onlineUsers: new Map()
})

const state = initialState()

const getters = {
  filteredTreeData (state) {
    if (!state.searchCropVal) {
      return state.treeData
    }
    return state.treeData.filter(el => el.name === state.searchCropVal)
  },
  cropList (state) {
    return state.cropList
  }
}

const mutations = {
  resetState (state) {
    const initial = initialState()
    Object.keys(initial).forEach(key => { state[key] = initial[key] })
  },
  resetUserStorage () {
    const suffix = ['own', 'ext']
    Object.keys(CACHE_KEY).forEach(key => {
      suffix.forEach(el => {
        Vue.ss.remove(`${CACHE_KEY[key]}_${el}`)
      })
    })
  },
  setDetailUserInfo (state, info) {
    state.detailUserInfo = info
  },
  setTreeData (state, data) {
    state.treeData = setTreeDataOfOnlineAndOffline(data, [...state.onlineUsers.values()])
  },
  setCropList (state, { myCorpInfo, list }) {
    state.cropList = list.concat([myCorpInfo])
  },
  setTreeDataOfOnlineAndOffline (state, userList = []) {
    state.treeData = setTreeDataOfOnlineAndOffline(state.treeData, userList)
    state.onlineUsers.forEach((value, key) => {
      if (value.status === 0) {
        state.onlineUsers.delete(key)
      }
    })
  },
  setHasLoadTree (state) {
    state.hasLoadTree = true
  },
  setCorpMapperTree (state, data) {
    state.corpMapperTree = [
      ...state.corpMapperTree,
      ...data
    ]
  },
  setSearchVal (state, val) {
    state.searchVal = val
  },
  setSearchCropVal (state, val) {
    state.searchCropVal = val
  },
  setOnlineUsers (state, list = []) {
    list.forEach(item => {
      state.onlineUsers.set(item.userid, item)
    })
  }
}

const actions = {
  setOnlineUsers({ state, commit }, list = []) {
    commit('setOnlineUsers', list)

    if (state.onlineLoading) return
    state.onlineLoading = true

    setTimeout(() => {
      commit('setTreeDataOfOnlineAndOffline', [...state.onlineUsers.values()])
      state.onlineLoading = false
    }, 1500)
  },
  async queryUserInfo ({ commit }, uid) {
    try {
      const result = await axios.get(`/hiapigw/app/user/someoneUserInfo?uid=${uid}&_id=` + Global.genNonce(5))
      if (result.data.code === 0) {
        commit('setDetailUserInfo', result.data.data.userInfo)
      } else {
        throw result.data.comment
      }
    } catch (err) {
      console.warn(err)
    }
  },
  // 获取协作企业列表
  getCorpList ({ commit }) {
    return new Promise((resolve, reject) => {
      return axios.get('/hiapigw/app/coc/corpList').then(res => {
        if (res.data.code === 0) {
          const { data: { list, myCorpInfo } } = res.data
          commit('setCropList', { list, myCorpInfo })
          resolve(list)
        }
      })
    })
  },
  // 获取原始树形结构数据
  getOriginTree (_, corpId) {
    return new Promise((resolve, reject) => {
      return axios.get(`/hiapigw/app/orgTree?corpId=${corpId}`).then(res => {
        if (res.data.code === 0) {
          resolve({ ...res.data.data.tree, corpId })
        }
      })
    })
  },
  // 获取用户列表
  getUserBasicList ({ state }, isOwnCompany) {
    const page = 1
    const limit = 100
    const userBasicList = []
    const hash = ''
    return queryUserBasicList(page, limit, userBasicList, hash, isOwnCompany).then(res => {
      [...state.onlineUsers.values()].filter(onLineUser => onLineUser.status).forEach(onLineUser => {
        let exist = res.find(user => user.uid === onLineUser.userid)
        if (exist) {
          exist = Object.assign(exist, { roomId: onLineUser.roomid, deviceType: onLineUser.devicetype })
        }
      })
      return res
    })
  },
  ownCompanyTree ({ rootState, commit, dispatch }) {
    return Promise.all([
      dispatch('getOriginTree', rootState.userInfo.corpId),
      dispatch('getUserBasicList', true)
    ]).then(async res => {
      commit('setCorpMapperTree', [{ corpId: rootState.userInfo.corpId, tree: clone(res[0]) }])
      return [await handleTree(res[0], res[1], 0)]
    })
  },
  extCompanyTree ({ commit, dispatch }) {
    return dispatch('getCorpList').then(res => {
      const corpIdList = res.map(el => el.corpId)
      const exConferenceList = res.map(el => dispatch('getOriginTree', el.corpId))
      return Promise.all(exConferenceList.concat(dispatch('getUserBasicList', false))).then(async res => {
        const extTreeData = []
        const corpMapperTree = []
        for (let i = 0; i < res.length - 1; i++) {
          corpMapperTree.push({ corpId: corpIdList[i], tree: clone(res[i]) })
          const x = await handleTree(res[i], res[res.length - 1], 0)
          extTreeData.push(x)
        }
        commit('setCorpMapperTree', corpMapperTree)
        return extTreeData
      })
    })
  },
  getCompanyTree ({ dispatch, commit, state }) {
    return Promise.all([dispatch('ownCompanyTree'), dispatch('extCompanyTree')]).then(res => {
      commit('setHasLoadTree')
      commit('setTreeData', res[0].concat(res[1]))
      dispatch('setOnlineUsers', [...state.onlineUsers.values()])
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
